﻿import { AttributeDefinition, AttributeType, AttributeValueChoice } from '@/shared/helpers/api/types/entities.ts';
import _ from 'lodash';
import { LibraryResponse } from '@/shared/helpers/api/types/codeEditContextResponse.ts';
import { CaseSensitiveCodeRefs, ReferenceFilterNode } from '@/shared/codeedit/dynamicFilterTypes.ts';
import { AttributeValue } from '@/shared/codeedit/codeEditFields.ts';

/*
Definition for the properties and attributes that are edited in an edit session
 */
export type FieldDefinition = {
    attribute?: boolean;
    defaultValue?: string | null;
    choices?: AttributeValueChoice[];
    description?: string | null;
    field: string;
    includeInIdentity: boolean;
    label: string | null;
    refLibraryName?: string | null;
    referenceDisplayMode?: number;
    referenceFilterStatic?: string;
    referenceFilterStaticErrorMessage?: string;
    referenceFilterDynamic?: ReferenceFilterNode | null;
    referenceFilterDynamicErrorMessage?: string;
    referenceFilterDynamicShowFiltered?: boolean;
    // The CodeRef fields that point into case-sensitive libraries, which the dynamic filter's CodeRef rules may compare
    referenceFilterDynamicCaseSensitiveCodeRefs?: CaseSensitiveCodeRefs;
    regex?: string | null;
    regexErrorMessage?: string | null;
    required: boolean;
    sequenceNumber?: number;
    textAreaLines?: number;
    isMarkdown?: boolean;
    type: AttributeType;
    mustBeUnique?: boolean;
    valueIdx?: number;
};

export function isStringField(fieldDefinition: FieldDefinition, value: AttributeValue): value is string | null {
    return fieldDefinition.type === 'String' && (value === null || typeof value === 'string');
}

export function isNumberField(fieldDefinition: FieldDefinition, value: AttributeValue): value is number | null {
    return ['Int', 'Float'].includes(fieldDefinition.type) && (value === null || typeof value === 'number');
}

type CreateCodeDefArrayOpts = {
    nameInIdentity?: boolean;
    nameMustBeUnique?: boolean;
    filterFunc?: () => boolean;
    descriptionIsRequired?: boolean;
    descriptionRegex?: string;
};

/*
Merges the attribute definitions and the default property definition of a code into an array of field definitions
 */
export function createFieldDefinitions(attributesDefs: AttributeDefinition[], options: CreateCodeDefArrayOpts = {}): FieldDefinition[] {
    const nameInIdentity = options.nameInIdentity ?? true;
    const nameMustBeUnique = options.nameMustBeUnique ?? false;
    const filterFunc = options.filterFunc ?? (() => true);
    const descriptionIsRequired = options.descriptionIsRequired ?? false;
    const descriptionRegex = options.descriptionRegex ?? '';

    const constDefs: FieldDefinition[] = [
        { field: 'identity', label: 'Identity', required: true, includeInIdentity: true, type: 'String' },
        {
            field: 'name',
            label: 'Name',
            required: true,
            includeInIdentity: nameInIdentity,
            mustBeUnique: nameMustBeUnique,
            type: 'String'
        },
        { field: 'isValid', label: 'Is valid', required: true, includeInIdentity: false, type: 'Bool' },
        {
            field: 'description',
            label: 'Description',
            required: descriptionIsRequired,
            includeInIdentity: false,
            type: 'String',
            regex: descriptionRegex
        },
        { field: 'dateCreated', label: 'Date created', required: true, includeInIdentity: false, type: 'DateTime' },
        { field: 'dateUpdated', label: 'Date modified', required: true, includeInIdentity: false, type: 'DateTime' },
        { field: 'attachmentKey', label: 'AttachmentKey', required: false, includeInIdentity: false, type: 'String' },
        {
            field: 'commonLibraryIRI',
            label: 'CommonLibraryIRI',
            required: false,
            includeInIdentity: false,
            type: 'String'
        }
    ];

    const defs: FieldDefinition[] = constDefs.concat(attributesDefs
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
        .map((ad: AttributeDefinition): FieldDefinition => {
            return {
                attribute: true,
                defaultValue: ad.defaultValue,
                choices: ad.choices,
                description: ad.description,
                field: ad.name,
                includeInIdentity: ad.includeInIdentity,
                label: ad.displayAs,
                refLibraryName: ad.referenceLibraryName,
                referenceDisplayMode: ad.referenceDisplayMode,
                referenceFilterStatic: ad.referenceFilterStatic,
                referenceFilterStaticErrorMessage: ad.referenceFilterStaticErrorMessage,
                referenceFilterDynamic: ad.referenceFilterDynamic,
                referenceFilterDynamicErrorMessage: ad.referenceFilterDynamicErrorMessage,
                referenceFilterDynamicShowFiltered: ad.referenceFilterDynamicShowFiltered,
                regex: ad.regex,
                regexErrorMessage: ad.regexErrorMessage,
                required: ad.required,
                sequenceNumber: ad.sequenceNumber,
                textAreaLines: ad.textAreaLines,
                isMarkdown: ad.isMarkdown,
                type: ad.attributeType
            };
        }))
        .filter(filterFunc);

    for (let i = 0; i < defs.length; i++) {
        defs[i].valueIdx = i;
    }

    return defs;
}

export function createFieldDefinitionsWithCustomLabels(library: LibraryResponse, customLabels?: Record<string, string>): FieldDefinition[] {
    const defArray = createFieldDefinitions(library.attributeDefinitions, {
        // Note: If name may not be changed, it must be treated as in identity (not editable)
        nameInIdentity: library.nameInIdentity || !library.nameMayChange,
        nameMustBeUnique: library.nameMustBeUnique,
        descriptionIsRequired: library.descriptionIsRequired
    });
    if (customLabels) {
        for (const key in customLabels) {
            const idx = _.findIndex(defArray, x => x.field === key);
            if (idx > -1) {
                defArray[idx].label = customLabels[key];
            }
        }
    }

    return defArray;
}

export function isHidden(fieldDefinition: FieldDefinition) {
    return ['identity', 'dateCreated', 'dateUpdated', 'commonLibraryIRI', 'attachmentKey'].includes(fieldDefinition.field);
}

export function fieldDefinitionFormInputType(fieldDefinition: FieldDefinition): string {
    switch (fieldDefinition.type) {
        case 'Int':
            return 'number';
        case 'Float':
            return 'number';
        case 'String':
            return 'text';
        case 'Date':
            return 'date';
        default:
            return 'text';
    }
}

export function fieldDefinitionFormStepSize(fieldDefinition: FieldDefinition, noDigits = 2): string {
    switch (fieldDefinition.type) {
        case 'Int':
            return '1';
        case 'Float': {
            const zero = '0';
            return `0.${zero.repeat(noDigits - 1)}1`;
        }
        default:
            return '1';
    }
}
